import { useState } from 'react';
import { useKnowledgeBases, KnowledgeBase } from '../context/KnowledgeBaseContext';
import { formatErrorMessage } from '../utils/formatErrorMessage';
import { FileUploadZone, UploadedFile } from './FileUploadZone';

interface CreateKnowledgeBaseModalProps {
  onClose: () => void;
}

export function CreateKnowledgeBaseModal({ onClose }: CreateKnowledgeBaseModalProps) {
  const { createKnowledgeBase, uploadFilesToKB } = useKnowledgeBases();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Remember a KB created on a previous attempt so retrying after an upload
  // failure doesn't create a duplicate knowledge base.
  const [createdKb, setCreatedKb] = useState<KnowledgeBase | null>(null);

  const handleFilesSelected = (files: File[]) => {
    setPendingFiles(prev => [...prev, ...files]);
    const newUploaded: UploadedFile[] = files.map(f => ({
      name: f.name,
      size: f.size,
      status: 'pending' as const,
    }));
    setUploadedFiles(prev => [...prev, ...newUploaded]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      let kb = createdKb;
      if (!kb) {
        kb = await createKnowledgeBase(name.trim(), description.trim() || undefined);
        setCreatedKb(kb);
      }
      if (pendingFiles.length > 0) {
        setUploadedFiles(prev => prev.map(f => ({ ...f, status: 'uploading' as const })));
        await uploadFilesToKB(kb.id, pendingFiles);
      }
      onClose();
    } catch (err) {
      setUploadedFiles(prev => prev.map(f => (f.status === 'uploading' ? { ...f, status: 'pending' as const } : f)));
      setError(
        formatErrorMessage(err instanceof Error ? err.message : 'Something went wrong.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-btn shadow-xl w-full max-w-lg mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
          <h2 className="text-base font-semibold text-neutral-800 font-heading">New Knowledge Base</h2>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-6 py-5 overflow-y-auto flex-1">
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">
              Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Clinical Protocols"
              className="w-full border border-neutral-300 rounded-btn px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              required
              disabled={submitting || !!createdKb}
            />
            {createdKb && (
              <p className="text-[11px] text-neutral-400 mt-1">
                Knowledge base already created — retry will upload the files into it.
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">
              Description <span className="text-neutral-400">(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Describe what documents this knowledge base contains..."
              rows={2}
              className="w-full border border-neutral-300 rounded-btn px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
              disabled={submitting}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">
              Files <span className="text-neutral-400">(optional)</span>
            </label>
            <FileUploadZone
              uploadedFiles={uploadedFiles}
              onFilesSelected={handleFilesSelected}
              disabled={submitting}
            />
          </div>

          {error && (
            <p className="text-xs text-red-500">{error}</p>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-2 border-t border-neutral-100 mt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !name.trim()}
              className="btn-primary px-5 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (createdKb ? 'Uploading...' : 'Creating...') : createdKb ? 'Retry Upload' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
